import {DataStatus, Tooltip} from 'platform/components';
import {Box, Icon, Stack, useElementSize} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {AftersalesPaymentMethod, useGetCheckoutPaymentsV2Query} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AftersalesCheckoutBillingInformation} from '../AftersalesCheckoutBillingInformation/AftersalesCheckoutBillingInformation';
import {AftersalesCheckoutPayment} from '../AftersalesCheckoutPayment/AftersalesCheckoutPayment';
import {AftersalesCheckoutBillingCard} from './components/AftersalesCheckoutBillingCard';
import {CheckoutCardFrame} from './components/CheckoutCardFrame';

// The payment form keeps four fields in a row, so a narrower checkout stacks the cards.
const SIDE_BY_SIDE_MIN_WIDTH = 1120;
// 400 px. The contract cards of the billing information overflow a narrower column.
const BILLING_MIN_WIDTH = 100;
// Side by side, the billing information takes 3/8 of the width and the payment 5/8.
const BILLING_GROW = 3;
const PAYMENT_GROW = 5;

interface CheckoutProps extends RequiredTestIdProps {
  checkoutId: string;
  defaultPaymentType?: AftersalesPaymentMethod;
  recordId?: string;
  resourceId?: EntityResourceIds;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  isBillingInformationEditingDisabled?: boolean;
  serviceCaseId?: string;
}

export function AftersalesCheckout(props: CheckoutProps) {
  const [containerRef, containerWidth] = useElementSize<HTMLDivElement>();

  const {data, isLoading, isError} = useGetCheckoutPaymentsV2Query(
    {checkoutId: props.checkoutId},
    {refetchOnMountOrArgChange: true}
  );

  const relevantPayments = data?.filter(
    ({discriminator}) => discriminator === 'BALANCE' || discriminator === 'DEPOSIT'
  );
  const areAllPaymentsPaid =
    relevantPayments && relevantPayments.length > 0
      ? relevantPayments.every(({paymentState}) => paymentState === 'PAID')
      : false;

  const isSideBySide = isNotNil(containerWidth) && containerWidth >= SIDE_BY_SIDE_MIN_WIDTH;

  return (
    <Box ref={containerRef}>
      {/* The cards wait for the first width measurement, so they mount once, in the right layout. */}
      <DataStatus isLoading={isLoading || isNil(containerWidth)} isError={isError}>
        <Stack direction={isSideBySide ? 'row' : 'column'} spacing={4}>
          <Box
            flexGrow={isSideBySide ? BILLING_GROW : undefined}
            flexBasis={isSideBySide ? 0 : undefined}
            minWidth={isSideBySide ? BILLING_MIN_WIDTH : undefined}
          >
            <AftersalesCheckoutBillingCard
              checkoutId={props.checkoutId}
              isSideBySide={isSideBySide}
              data-testid={suffixTestId('billingInformationCard', props)}
            >
              <AftersalesCheckoutBillingInformation
                checkoutId={props.checkoutId}
                recordId={props.recordId}
                resourceId={props.resourceId}
                isEditingDisabled={
                  props.isEditingDisabled || props.isBillingInformationEditingDisabled
                }
                data-testid={props['data-testid']}
              />
            </AftersalesCheckoutBillingCard>
          </Box>
          <Box
            flexGrow={isSideBySide ? PAYMENT_GROW : undefined}
            flexBasis={isSideBySide ? 0 : undefined}
            minWidth={0}
          >
            {/* The same element in both layouts, so a width change keeps the payment form. */}
            <CheckoutCardFrame
              variant="inlineGrey"
              title={i18n.t('entity.checkout.labels.payment')}
              titleAddon={
                <Tooltip
                  label={
                    areAllPaymentsPaid
                      ? i18n.t('entity.checkout.labels.allPaymentsPaid')
                      : i18n.t('general.labels.pending')
                  }
                  data-testid={suffixTestId('paymentStateTooltip', props)}
                >
                  <Icon
                    value={areAllPaymentsPaid ? 'action/check_circle' : 'action/watch_later'}
                    color={areAllPaymentsPaid ? 'severity.success' : 'severity.warning'}
                    size={4}
                    data-testid={suffixTestId('paymentState', props)}
                  />
                </Tooltip>
              }
              isFullHeight={isSideBySide}
              data-testid={suffixTestId('paymentCard', props)}
            >
              <AftersalesCheckoutPayment
                checkoutId={props.checkoutId}
                serviceCaseId={props.serviceCaseId}
                serviceOrderId={props.recordId}
                data-testid={props['data-testid']}
                defaultPaymentType={props.defaultPaymentType}
                isEditingDisabled={props.isEditingDisabled}
                isCompletingDisabled={props.isCompletingDisabled}
              />
            </CheckoutCardFrame>
          </Box>
        </Stack>
      </DataStatus>
    </Box>
  );
}
